import { Button } from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import { default as _ReactPlayer } from 'react-player/lazy'
import { ReactPlayerProps } from 'react-player/types/lib'

import { FilterSvg } from '../../../assets/filter'
import { MUMRecordObj } from '../../../providers/MUM/MUMProvider'
import { BACObj } from '../../../providers/PatientChartPage/BACProvider'
import { themeColors } from '../../../theme'
import { useStyles } from '../PatientChartPage.styles'
import { FlagBacMumForIncidentModal } from './FlagBacMumForIncident'
import { ReviewBacOrDrinkTypesModal } from './ReviewBacOrDrinkTypesModal'

interface BacMumTableInterface {
  routedPatientId: string | undefined
  selectedReviewOption: string
  reviewTableTabs: string[]
  filteredMUMVideos?: MUMRecordObj[]
  filteredBacWithVideos?: BACObj[]
  videoReviewFilter: boolean
  setVideoReviewFilter: React.Dispatch<React.SetStateAction<boolean>>
  setFilteredBacWithVideos?: React.Dispatch<React.SetStateAction<BACObj[]>>
  setFilteredMUMVideos?: React.Dispatch<React.SetStateAction<MUMRecordObj[]>>
  showReviewModal: boolean
  setShowReviewModal: React.Dispatch<React.SetStateAction<boolean>>
}

export const ReviewBacMumTable = ({
  routedPatientId,
  selectedReviewOption,
  reviewTableTabs,
  filteredMUMVideos,
  filteredBacWithVideos,
  videoReviewFilter,
  setVideoReviewFilter,
  setFilteredBacWithVideos,
  setFilteredMUMVideos,
  showReviewModal,
  setShowReviewModal,
}: BacMumTableInterface) => {
  const { classes } = useStyles()

  const ReactPlayer = _ReactPlayer as unknown as React.FC<ReactPlayerProps>

  const [showVideo, setShowVideo] = useState(0)

  const [showFlagForIncidentModal, setShowFlagForIncidentModal] =
    useState(false)

  const renderVideosAccordingly = () => {
    if (selectedReviewOption === 'Review BAC' && filteredBacWithVideos) {
      return filteredBacWithVideos
    }
    if (selectedReviewOption === 'Review MUM' && filteredMUMVideos) {
      return filteredMUMVideos
    }
  }

  return (
    <table className={classes.reviewTable}>
      <caption className={classes.tableCaption}>
        Click Date to view video recording.
      </caption>
      <thead className={classes.tableThead}>
        <tr>
          {reviewTableTabs.map((r: string) => {
            return (
              <th
                className={classes.tableLabelTh}
                scope='col'
                key={`ReviewTable-${r}`}
              >
                {r === 'Reviewed' ? (
                  <button
                    type='button'
                    className={classes.tableSortBtn}
                    onClick={() => {
                      setVideoReviewFilter(!videoReviewFilter)
                    }}
                  >
                    <span className={classes.tableSortTxt}>{r}</span>
                    <FilterSvg />
                  </button>
                ) : (
                  r
                )}
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        {renderVideosAccordingly()?.map((r: any, i) => {
          return (
            <tr key={`table-video-${i}`}>
              <th className={classes.tableTh} scope='col'>
                {r.reviewed || !r.videoURL ? (
                  dayjs(r.recordDate).format('MM/DD/YYYY')
                ) : (
                  <button
                    type='button'
                    onClick={() => {
                      showVideo === r.id ? setShowVideo(0) : setShowVideo(r.id)
                    }}
                    className={classes.videoLinkBtn}
                  >
                    {dayjs(r.recordDate).format('MM/DD/YYYY')}
                  </button>
                )}
              </th>
              <th className={classes.tableTh} scope='col'>
                {dayjs(r.recordDate).format('h:mm:ss A')}
              </th>
              {selectedReviewOption === 'Review BAC' ? (
                <th className={classes.tableTh} scope='col'>
                  {r.value}
                </th>
              ) : null}
              <th className={classes.tableTh} scope='col'>
                {r.reviewed
                  ? 'Reviewed'
                  : `${r.videoURL ? 'Needs Review' : 'No Video'}`}
              </th>
              <th className={classes.tableTh} scope='col'>
                {r.reviewed ? r.reviewedBy : 'None'}
              </th>

              {showVideo === r.id && r.videoURL && !r.reviewed && (
                <div className={classes.playerWrapper}>
                  <ReactPlayer
                    controls
                    className='react-player'
                    url={r.videoURL}
                    width='100%'
                    height='80%'
                  />
                  <Button
                    onClick={() => {
                      setShowReviewModal(true)
                    }}
                    variant='contained'
                    style={{
                      fontSize: '16px',
                      marginTop: '10px',
                      width: '180px',
                      backgroundColor: themeColors.red,
                      borderColor: 'transparent',
                    }}
                  >
                    Mark as Reviewed
                  </Button>

                  <Button
                    onClick={() => {
                      setShowFlagForIncidentModal(true)
                    }}
                    variant='contained'
                    style={{
                      fontSize: '16px',
                      marginTop: '10px',
                      width: '180px',
                      backgroundColor: themeColors.red,
                      borderColor: 'transparent',
                    }}
                  >
                    Flag for Incident
                  </Button>

                  <ReviewBacOrDrinkTypesModal
                    routedPatientId={routedPatientId}
                    recordId={r.id}
                    showReviewModal={showReviewModal}
                    selectedReviewOption={selectedReviewOption}
                    setShowReviewModal={setShowReviewModal}
                    setFilteredDataWithVideos={
                      selectedReviewOption === 'Review BAC' &&
                      setFilteredBacWithVideos
                        ? setFilteredBacWithVideos
                        : setFilteredMUMVideos
                        ? setFilteredMUMVideos
                        : null
                    }
                  />

                  <FlagBacMumForIncidentModal
                    routedPatientId={routedPatientId}
                    recordId={r.id}
                    selectedReviewOption={selectedReviewOption}
                    showFlagForIncidentModal={showFlagForIncidentModal}
                    setShowFlagForIncidentModal={setShowFlagForIncidentModal}
                    setFilteredDataWithVideos={
                      selectedReviewOption === 'Review BAC' &&
                      setFilteredBacWithVideos
                        ? setFilteredBacWithVideos
                        : setFilteredMUMVideos
                        ? setFilteredMUMVideos
                        : null
                    }
                  />
                </div>
              )}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
